import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { PricingPlans } from '@/components/PricingPlans';
import { Logo } from '@/components/Logo';
import { Loader2, Sparkles } from 'lucide-react';
import { logger } from '@/lib/logger';
import { Badge } from '@/components/ui/badge';

export default function SelectPlan() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [autoCheckoutPlan, setAutoCheckoutPlan] = useState<string | null>(null);
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'annual'>(
    new URLSearchParams(window.location.search).get('billing') === 'annual' ? 'annual' : 'monthly'
  );
  const [subscription, setSubscription] = useState<{ status: string; plan_tier: string } | null>(null);

  // Get plan, billing frequency, and checkout flag from URL params if present
  const searchParams = new URLSearchParams(window.location.search);
  const preSelectedPlan = searchParams.get('plan');

  useEffect(() => {
    checkUserAndSubscription();
    fetchLogo();
    // Store pre-selected plan for auto-checkout after loading
    if (preSelectedPlan) {
      setAutoCheckoutPlan(preSelectedPlan);
    }
  }, [preSelectedPlan]);

  // Set up realtime subscription to redirect to dashboard when subscription is created
  // This handles cases where user gets redirected to select-plan before webhook creates subscription
  useEffect(() => {
    const setupRealtimeListener = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile?.team_id) return;

      const channel = supabase
        .channel(`select-plan-subscription-${profile.team_id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'team_subscriptions',
            filter: `team_id=eq.${profile.team_id}`,
          },
          async (payload) => {
            logger.info('Subscription created/updated on select-plan page, checking if should redirect...');
            // Re-check subscription and redirect if active
            const { data: subData } = await supabase
              .from('team_subscriptions')
              .select('status, current_period_end, polar_subscription_id, trial_ends_at')
              .eq('team_id', profile.team_id)
              .maybeSingle();

            if (subData) {
              const { hasActiveSubscription } = await import('@/utils/subscriptionAccess');
              const hasAccess = hasActiveSubscription(subData.status, subData.current_period_end, subData.trial_ends_at);
              if (hasAccess) {
                logger.info('Active subscription detected, redirecting to dashboard');
                navigate('/dashboard');
              }
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    const cleanup = setupRealtimeListener();
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
    };
  }, [navigate]);

  const fetchLogo = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('north_logo_url')
      .not('north_logo_url', 'is', null)
      .limit(1)
      .maybeSingle();
    
    if (data?.north_logo_url) {
      setLogoUrl(data.north_logo_url);
    }
  };

  const checkUserAndSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        navigate('/login');
        return;
      }

      setUserEmail(user.email || null);

      // Check if user already has a subscription via their team
      // Note: For new OAuth users, give the database trigger time to create profile/team/subscription
      let retries = 3;
      let profile = null;

      while (retries > 0) {
        const { data, error } = await supabase
          .from('profiles')
          .select('team_id')
          .eq('user_id', user.id)
          .maybeSingle();  // Use maybeSingle instead of single to avoid 406 on missing profile

        if (data?.team_id) {
          profile = data;
          break;
        }

        // If no profile yet (new user, trigger still processing), wait and retry
        if (error && error.code !== 'PGRST116') {  // PGRST116 = no rows returned
          logger.error('Error fetching profile:', error);
        }
        
        if (!data?.team_id) {
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } else {
          break;
        }
      }

      if (profile?.team_id) {
        // Check if this user was invited (has an accepted invitation)
        // Invited users should go to dashboard, not select-plan
        const { data: acceptedInvitation } = await supabase
          .from('team_invitations')
          .select('id, status')
          .ilike('email', user.email || '')
          .in('status', ['accepted', 'pending'])
          .maybeSingle();

        // If user was invited, redirect to dashboard (team already has subscription)
        if (acceptedInvitation) {
          navigate('/dashboard');
          return;
        }

        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('team_subscriptions')
          .select('plan_tier, status, current_period_end, polar_subscription_id, trial_ends_at')
          .eq('team_id', profile.team_id)
          .maybeSingle();

        // Only set subscription if it has a Polar subscription ID (meaning it's an active subscription)
        // If no polar_subscription_id, user hasn't completed checkout yet
        if (subscriptionData?.polar_subscription_id) {
          setSubscription(subscriptionData);
        } else {
          setSubscription(null);
        }

        // If subscription query fails, log but continue (user can still select plan)
        if (subscriptionError && subscriptionError.code !== 'PGRST116') {
          logger.error('Error fetching subscription:', subscriptionError);
        }

        // If subscription exists but no Polar subscription ID, wait for it to be created
        // The auth hook should create it automatically - we don't want to create duplicates!
        if (subscriptionData && !subscriptionData.polar_subscription_id) {
          logger.info('No Polar subscription found yet, waiting for auth hook to complete...');

          // Wait for auth hook to complete (it may still be processing)
          // Poll for up to 10 seconds to avoid race condition / duplicate subscriptions
          let attempts = 10;
          let foundSubscription = false;

          while (attempts > 0 && !foundSubscription) {
            await new Promise(resolve => setTimeout(resolve, 1000));

            const { data: refreshedSub } = await supabase
              .from('team_subscriptions')
              .select('polar_subscription_id')
              .eq('team_id', profile.team_id)
              .single();

            if (refreshedSub?.polar_subscription_id) {
              logger.info('Polar subscription created successfully:', refreshedSub.polar_subscription_id);
              foundSubscription = true;
            } else {
              attempts--;
              logger.info(`Waiting for Polar subscription... (${attempts} attempts left)`);
            }
          }

          if (!foundSubscription) {
            logger.warn('Polar subscription not created after 10 seconds - auth hook may have failed');
            // Continue anyway - user can still proceed with plan selection
            // NOTE: We intentionally don't create a subscription here to avoid duplicates
            // If the auth hook truly failed, the user can contact support
          }
        }

        // If user has an active subscription WITH a Polar subscription ID, redirect to dashboard
        // Only redirect if they have a real Polar subscription (not just a database record)
        if (subscriptionData?.polar_subscription_id && (subscriptionData.status === 'active' || subscriptionData.status === 'trialing')) {
          navigate('/dashboard');
          return;
        }
        
        // If subscription exists but has no Polar subscription ID, it's likely still processing
        // Check if it's active - if so, redirect to dashboard (webhook might still be updating polar_subscription_id)
        if (subscriptionData && !subscriptionData.polar_subscription_id) {
          const { hasActiveSubscription } = await import('@/utils/subscriptionAccess');
          const hasAccess = hasActiveSubscription(subscriptionData.status, subscriptionData.current_period_end, subscriptionData.trial_ends_at);
          if (hasAccess) {
            // Subscription is active, redirect to dashboard even without polar_subscription_id
            logger.info('Subscription is active but missing polar_subscription_id - redirecting to dashboard (webhook may still be processing)');
            navigate('/dashboard');
            return;
          } else {
            logger.info('Subscription record exists but has no Polar subscription ID and is not active - allowing plan selection');
          }
        }
      }

      // If still no team_id after retries, user needs to select a plan (this is the expected flow)
      // Always set loading to false at the end, even if we're redirecting
      setLoading(false);
    } catch (error) {
      logger.error('Error checking subscription:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-background via-muted/20 to-accent/10">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-accent/10 relative overflow-hidden">
      {/* Static background elements - removed animate-pulse to prevent flashing */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-accent/10 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-40" />
      </div>

      <div className="container mx-auto px-6 py-8 md:py-12 relative z-10">
        {/* Logo - using Logo component like on Landing page */}
        <div className="flex justify-center mb-8 animate-fade-in">
          <div className="relative">
            <div className="absolute -inset-2 bg-gradient-to-r from-accent/20 to-primary/20 rounded-lg blur opacity-50" />
            <div className="relative bg-background/80 backdrop-blur-sm px-8 py-4 rounded-lg border border-border/50">
              <Logo customLogoUrl={logoUrl} size="lg" />
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-12 animate-fade-in space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-4">
            <Sparkles className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium text-accent">Welcome to North Reports</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-primary">
            {subscription?.status === 'trialing' ? 'Upgrade Your Trial' : 'Choose Your Plan'}
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            {subscription?.status === 'trialing' 
              ? 'Upgrade to unlock more North Reports and features'
              : 'Start creating stunning seller reports that showcase your marketing excellence'}
          </p>

          {userEmail && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 backdrop-blur-sm border border-border">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <p className="text-sm text-muted-foreground">
                {userEmail}
              </p>
            </div>
          )}
        </div>

        {/* Plans Section with Toggle */}
        <div className="animate-scale-in space-y-4">
          {/* Toggle Header Row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 max-w-7xl mx-auto">
            <div>
              <h2 className="text-2xl font-bold">Available Plans</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Select the plan that best fits your needs
              </p>
            </div>
            <div className="flex items-center gap-3">
              {billingFrequency === 'monthly' ? (
                <span className="text-sm text-muted-foreground">Pay annually and save</span>
              ) : (
                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-300">
                  <Badge className="bg-green-600 hover:bg-green-600 text-white px-3 py-1">
                    1 Month Free
                  </Badge>
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">Save up to $249/year</span>
                </div>
              )}
              <div className="relative bg-primary/10 p-1 rounded-full inline-flex border border-primary/30">
                <button
                  onClick={() => setBillingFrequency('monthly')}
                  className={`relative px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                    billingFrequency === 'monthly'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-primary/70 hover:text-primary'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingFrequency('annual')}
                  className={`relative px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                    billingFrequency === 'annual'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-primary/70 hover:text-primary'
                  }`}
                >
                  Annual
                </button>
              </div>
            </div>
          </div>

          <PricingPlans
            currentPlan={subscription?.plan_tier && subscription?.polar_subscription_id ? subscription.plan_tier : undefined}
            autoTriggerPlan={autoCheckoutPlan}
            hideToggle={true}
            externalBillingFrequency={billingFrequency}
            onBillingFrequencyChange={setBillingFrequency}
          />
        </div>
      </div>
    </div>
  );
}
